import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { normalizeFontWeight } from '../gallery/lib/normalize-font-weight';
import { unique } from '../lib/unique';
import { PICFLOW_CONFIG } from '../picflow-config';
import { ObservableDataLoaderService } from './observable-data-loader.service';

// "100",
// "200",
// "300",
// "regular",
// "500",
// "600",
// "700",
// "800",
// "900",
// "100italic",
// "200italic",
// "300italic",
// "italic",
// "500italic",
// "600italic",
// "700italic",
// "800italic",
// "900italic"

export interface GoogleFontDetails {
  family: string;
  variants: string[];
}

export const AkkuratAsGoogleFontDetails: GoogleFontDetails = {
  family: 'Picflow Type',
  variants: ['Regular', 'Bold', '900'],
};

@Injectable({
  providedIn: 'root',
})
export class GoogleFontService {
  private config = inject(PICFLOW_CONFIG);
  private http = inject(HttpClient);
  private loader = inject(ObservableDataLoaderService);

  getFontDetails(fontFamily: string): Observable<GoogleFontDetails> {
    if (!fontFamily || fontFamily === 'AkkuratLL') return of(AkkuratAsGoogleFontDetails);
    return this.loader.get(`font-details-${fontFamily}`, () => {
      return this.http
        .get<{ items: GoogleFontDetails[] }>(`${this.config.internalApiUrlBase}/font-details/${fontFamily}`)
        .pipe(
          map(response => response.items[0]),
          catchError(() => {
            return of(<GoogleFontDetails>{
              family: fontFamily,
              variants: [],
            });
          })
        );
    });
  }

  public getFontWeights(fontFamily: string): Observable<string[]> {
    return this.getFontDetails(fontFamily).pipe(
      map(details => {
        return unique(
          details.variants
            .map(variant => variant.replace(/italic$/, ''))
            .filter(Boolean)
            .map(variant => normalizeFontWeight(variant.toLowerCase()))
        );
      })
    );
  }

  public hasItalic(fontFamily: string): Observable<boolean> {
    return this.getFontDetails(fontFamily).pipe(
      map(details => {
        return details.variants.some(variant => variant.includes('italic'));
      })
    );
  }
}
